import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Users, TrendingUp, UserPlus, UserCheck, UserMinus,
  Activity, Calendar, Map, Filter, ArrowUp, ArrowDown,
  Globe, MapPin, PieChart, BarChart2, LineChart as LineChartIcon
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar, PieChart as RechartPieChart, Pie, Cell
} from "recharts";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { getAdminUserAnalytics } from "../../redux/Actions/AdminAction";

// Mock data for visualizations
const userTrends = [
  { month: "Jan", total: 1200, active: 950, new: 250 },
  { month: "Feb", total: 1500, active: 1200, new: 300 },
  { month: "Mar", total: 1800, active: 1400, new: 400 },
  { month: "Apr", total: 2200, active: 1800, new: 450 },
  { month: "May", total: 2800, active: 2200, new: 600 },
  { month: "Jun", total: 3200, active: 2600, new: 550 }
];

const userSegmentation = [
  { name: "Clients", value: 60, color: "#2563eb" },
  { name: "Local Professionals", value: 25, color: "#16a34a" },
  { name: "International Professionals", value: 15, color: "#9333ea" }
];

const retentionData = [
  { month: "Jan", value: 85 },
  { month: "Feb", value: 82 },
  { month: "Mar", value: 88 },
  { month: "Apr", value: 86 },
  { month: "May", value: 89 },
  { month: "Jun", value: 92 }
];

const UserAnalytics = () => {
  const [timeRange, setTimeRange] = useState("month");
  const [showFilters, setShowFilters] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  
  // Redux state and dispatch
  const dispatch = useDispatch();
  const { userAnalytics } = useSelector((state: any) => state.admin);

  // Fetch user analytics data on component mount and when time range changes
  useEffect(() => {
    setIsLoadingData(true);
    dispatch(getAdminUserAnalytics({ isShowLoader: false, period: timeRange }) as any)
      .finally(() => setIsLoadingData(false));
  }, [dispatch, timeRange]);

  // Use real API data or fallback to mock data
  const userTrends = userAnalytics?.userTrends || [];
  
  // Filter data based on time range
  const getFilteredData = () => {
    if (userTrends.length === 0) return [];
    
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    // Get current month name
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                       "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const currentMonthName = monthNames[currentMonth];
    
    switch (timeRange) {
      case "day":
        // Return last 7 days of data
        return userTrends.slice(-7);
      case "week":
        // Return last 4 weeks of data
        return userTrends.slice(-4);
      case "month":
        // Return last 6 months of data
        return userTrends.slice(-6);
      case "quarter":
        // Return last 12 months of data
        return userTrends.slice(-12);
      case "year":
        // Return all data
        return userTrends;
      default:
        return userTrends;
    }
  };

  const filteredUserTrends = getFilteredData();
  
  // Get current month data for stats
  const getCurrentMonthData = () => {
    if (filteredUserTrends.length === 0) return null;
    
    // Get the most recent month with data
    const currentMonth = filteredUserTrends[filteredUserTrends.length - 1];
    return currentMonth;
  };

  const currentMonthData = getCurrentMonthData();

  // Calculate totals from filtered data
  const totalUsers = filteredUserTrends.length > 0 ? filteredUserTrends.reduce((sum, item) => sum + item.total, 0) : 0;
  const totalActiveUsers = filteredUserTrends.length > 0 ? filteredUserTrends.reduce((sum, item) => sum + item.active, 0) : 0;
  const totalNewUsers = filteredUserTrends.length > 0 ? filteredUserTrends.reduce((sum, item) => sum + item.new, 0) : 0;

  // Calculate growth metrics for each stat
  const getGrowthMetrics = () => {
    if (filteredUserTrends.length < 2) {
      return {
        totalUsersGrowth: { percentage: 0, isPositive: true },
        activeUsersGrowth: { percentage: 0, isPositive: true },
        newUsersGrowth: { percentage: 0, isPositive: true }
      };
    }

    const current = filteredUserTrends[filteredUserTrends.length - 1];
    const previous = filteredUserTrends[filteredUserTrends.length - 2];

    return {
      totalUsersGrowth: {
        percentage: current.percentageChange || 0,
        isPositive: current.isPositive
      },
      activeUsersGrowth: {
        percentage: current.percentageChange || 0,
        isPositive: current.isPositive
      },
      newUsersGrowth: {
        percentage: current.percentageChange || 0,
        isPositive: current.isPositive
      }
    };
  };

  const growthMetrics = getGrowthMetrics();

  // Stats cards data
  const stats = [
    {
      title: "Total Users",
      value: totalUsers.toLocaleString(),
      change: `${growthMetrics.totalUsersGrowth.isPositive ? "+" : ""}${growthMetrics.totalUsersGrowth.percentage.toFixed(1)}%`,
      trend: growthMetrics.totalUsersGrowth.isPositive ? "up" : "down",
      icon: Users,
      color: "blue"
    },
    {
      title: "Active Users",
      value: totalActiveUsers.toLocaleString(),
      change: `${growthMetrics.activeUsersGrowth.isPositive ? "+" : ""}${growthMetrics.activeUsersGrowth.percentage.toFixed(1)}%`,
      trend: growthMetrics.activeUsersGrowth.isPositive ? "up" : "down",
      icon: UserCheck,
      color: "green"
    },
    {
      title: "New Users",
      value: totalNewUsers.toLocaleString(),
      change: `${growthMetrics.newUsersGrowth.isPositive ? "+" : ""}${growthMetrics.newUsersGrowth.percentage.toFixed(1)}%`,
      trend: growthMetrics.newUsersGrowth.isPositive ? "up" : "down",
      icon: UserPlus,
      color: "purple"
    },
    {
      title: "Current Month",
      value: currentMonthData ? currentMonthData.month : "N/A",
      change: currentMonthData ? `${currentMonthData.isPositive ? "+" : ""}${currentMonthData.percentageChange?.toFixed(1) || "0"}%` : "0%",
      trend: currentMonthData?.isPositive ? "up" : "down",
      icon: UserMinus,
      color: "red"
    }
  ];

  // Regional distribution - using current month data
  const regions = currentMonthData ? [
    { 
      name: "Total Users", 
      users: currentMonthData.total, 
      change: `${currentMonthData.isPositive ? "+" : ""}${currentMonthData.percentageChange?.toFixed(1) || "0"}%`,
      isPositive: currentMonthData.isPositive
    },
    { 
      name: "Active Users", 
      users: currentMonthData.active, 
      change: `${currentMonthData.isPositive ? "+" : ""}${currentMonthData.percentageChange?.toFixed(1) || "0"}%`,
      isPositive: currentMonthData.isPositive
    },
    { 
      name: "New Users", 
      users: currentMonthData.new, 
      change: `${currentMonthData.isPositive ? "+" : ""}${currentMonthData.percentageChange?.toFixed(1) || "0"}%`,
      isPositive: currentMonthData.isPositive
    }
  ] : [];

  // Show loading state if no data yet or loading
  if (isLoadingData || !userAnalytics) {
    return (
      <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
        <AdminHeader />
        <main className="lg:pl-64 pt-16">
          <div className="p-6">
            <div className="max-w-7xl mx-auto space-y-6">
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">
                    {isLoadingData ? `Loading ${timeRange} user analytics...` : "Loading user analytics..."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50"  style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">User Analytics</h1>
                <p className="text-gray-600 mt-1">Monitor and analyze user behavior and growth</p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                </Button>
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="day">Last 24 Hours</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="quarter">Last Quarter</option>
                  <option value="year">Last Year</option>
                </select>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {stats.map((stat, index) => (
                <Card key={index} className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-600">{stat.title}</p>
                      <h3 className="text-2xl font-semibold mt-1">{stat.value}</h3>
                      <div className={`flex items-center mt-1 ${
                        stat.trend === "up" ? "text-green-600" : "text-red-600"
                      }`}>
                        {stat.trend === "up" ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )}
                        <span className="ml-1">{stat.change}</span>
                      </div>
                    </div>
                    <div className={`p-2 bg-${stat.color}-100 rounded-lg`}>
                      <stat.icon className={`h-5 w-5 text-${stat.color}-600`} />
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* User Growth Trends */}
              <Card className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-lg font-semibold">User Growth Trends</h2>
                    <p className="text-sm text-gray-600">Monthly user acquisition and activity</p>
                  </div>
                  <Button variant="outline" size="sm">
                    Export
                  </Button>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={filteredUserTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value, name) => [value, name]} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="total" 
                        stroke="#2563eb" 
                        strokeWidth={2}
                        name="Total Users"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="active" 
                        stroke="#16a34a" 
                        strokeWidth={2}
                        name="Active Users"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="new" 
                        stroke="#9333ea" 
                        strokeWidth={2}
                        name="New Users"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* User Segmentation */}
              <Card className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-lg font-semibold">User Segmentation</h2>
                    <p className="text-sm text-gray-600">Distribution of user types</p>
                  </div>
                  <Button variant="outline" size="sm">
                    Export
                  </Button>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartPieChart>
                      <Pie
                        data={userSegmentation}
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={120}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {userSegmentation.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartPieChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* User Growth Rate */}
              <Card className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-lg font-semibold">User Growth Rate</h2>
                    <p className="text-sm text-gray-600">Monthly growth percentage</p>
                  </div>
                  <Button variant="outline" size="sm">
                    Export
                  </Button>
                </div>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={filteredUserTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`${value}%`, "Growth Rate"]} />
                      <Bar dataKey="percentageChange" fill="#2563eb">
                        {filteredUserTrends.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.isPositive ? "#16a34a" : "#dc2626"}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              {/* Current Month Breakdown */}
              <Card className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-lg font-semibold">Current Month Breakdown</h2>
                    <p className="text-sm text-gray-600">User metrics for {currentMonthData?.month || "current month"}</p>
                  </div>
                  <Button variant="outline" size="sm">
                    Export
                  </Button>
                </div>
                <div className="space-y-4">
                  {regions.map((region, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <MapPin className="h-5 w-5 text-gray-400" />
                        <div>
                          <p className="font-medium">{region.name}</p>
                          <p className="text-sm text-gray-500">{region.users.toLocaleString()} users</p>
                        </div>
                      </div>
                      <Badge 
                        variant={undefined} 
                        className={`${region.isPositive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
                      >
                        {region.change}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <br></br>
      <br></br>
      <br></br>
      <br></br>
      {/* <SharedFooter3/> */}
    </div>
  );
};

export default UserAnalytics;